import React, { useCallback, useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import { Stack, Typography } from '@mui/material';

import { brands } from '~/consts/brands';
import { ROLE_CUSTOM_OFFER_ADMIN_USER } from '~/consts/roles';

import useCurrentUser from '~/hooks/useCurrentUser';

import OffersService from '~/services/OffersService';

import PermissionedComponent from '../../PermissionedComponent';
import { validateVccReservationRequestParams } from '../utils';

import { CustomOfferVendorVirtualCreditCardLoading } from './CustomOfferVendorVirtualCreditCardLoading';
import { VCCForCustomOfferPreConditions } from './VCCForCustomOfferPreConditions';
import { VirtualCreditCardComponent } from './VirtualCreditCardComponent';
import { VirtualCreditCardState } from './VirtualCreditCardState';
import { useFetchVirtualCreditCardsForCustomOffer } from './hooks/useFetchVirtualCreditCardsForCustomOffer';
import { useReserveVirtualCreditCardsForCustomOffer } from './hooks/useReserveVirtualCreditCardsForCustomOffer';

type Props = {
  paramsForVccForCustomOffer: {
    hasVCC: boolean;
    customOfferId: string | null;
    customOfferStatus: App.CustomOffer['status'];
    currencyCode: string;
    costPrice: number;
    guestName: string;
    validityTo: string;
    paymentTermsGlobal: 'eft' | 'vcc';
    saveCustomOffer: () => Promise<void>;
  };
};

export default function CustomOfferVendorVirtualCreditCardSection({ paramsForVccForCustomOffer }: Props) {
  const {
    hasVCC,
    customOfferId,
    customOfferStatus,
    currencyCode,
    costPrice,
    guestName,
    validityTo,
    paymentTermsGlobal,
    saveCustomOffer,
  } = paramsForVccForCustomOffer;

  const brand = useSelector((state: App.State) => state.tenant.brand);
  const brandName = brands.find((b) => b.value === brand)?.title ?? 'Luxury Escapes';
  const [validationErrors, setValidationErrors] = useState<Array<string>>([]);

  const { virtualCreditCards, isLoading, error, fetchVirtualCreditCards } =
    useFetchVirtualCreditCardsForCustomOffer(customOfferId);

  const {
    virtualCreditCards: reservedVirtualCreditCards,
    error: reservationError,
    isLoading: isReservingVirtualCreditCards,
    reserveVirtualCreditCardsForCustomOffer,
  } = useReserveVirtualCreditCardsForCustomOffer(customOfferId);

  const [vccs, setVccs] = useState([]);

  const { user } = useCurrentUser();
  const isCustomOfferAdminUser = user?.roles?.includes(ROLE_CUSTOM_OFFER_ADMIN_USER);

  const handleReserveVirtualCreditCards = useCallback(async () => {
    // Redundant validation to avoid API failure.
    const errors = validateVccReservationRequestParams(
      customOfferStatus,
      currencyCode,
      costPrice,
      validityTo,
      guestName,
    );
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Reserve the VCCs for the custom offer.
    await reserveVirtualCreditCardsForCustomOffer();

    // Then save the custom offer. This will update custom offer snapshot in orders table.
    // i.e has_vcc flag in the orders table.
    await saveCustomOffer();
  }, [
    costPrice,
    currencyCode,
    customOfferStatus,
    guestName,
    reserveVirtualCreditCardsForCustomOffer,
    saveCustomOffer,
    validityTo,
  ]);

  // fetch the vccs for this custom offer.
  useEffect(() => {
    if (!hasVCC) {
      setVccs([]);
      return;
    }

    if (isCustomOfferAdminUser && hasVCC && customOfferId && customOfferStatus.toLowerCase() !== 'draft') {
      fetchVirtualCreditCards();
    }
  }, [fetchVirtualCreditCards, customOfferId, customOfferStatus, user.roles, hasVCC, isCustomOfferAdminUser]);

  // Initial render, check for existing VCCs.
  useEffect(() => {
    if (virtualCreditCards) {
      setVccs(virtualCreditCards);
    }
  }, [virtualCreditCards]);

  // VCC is manually reserved. Update state for re-render.
  useEffect(() => {
    if (reservedVirtualCreditCards) {
      setVccs(reservedVirtualCreditCards);
    }
  }, [reservedVirtualCreditCards]);

  // When VCC is reserved, VCC confirmation happens async. Hence fetch the VCCs every 3 seconds until card is confirmed or max attempts reached.
  useEffect(() => {
    const isConfirmedOrCancelled = [VirtualCreditCardState.CONFIRMED, VirtualCreditCardState.CANCELLED].includes(
      vccs[0]?.state,
    );

    if (isConfirmedOrCancelled || vccs?.length === 0) {
      return;
    }

    let attempts = 0;
    const maxAttempts = 5;
    const refreshInterval = 1000 * 3;

    const interval = setInterval(async () => {
      attempts++;
      const response = await OffersService.getCustomOfferVendorVirtualCreditCards(customOfferId);
      const card = response?.result?.[0];

      // Check if any card is confirmed or max attempts reached
      const isConfirmed = card?.state === VirtualCreditCardState.CONFIRMED;
      if (isConfirmed || attempts >= maxAttempts) {
        clearInterval(interval);
        setVccs([card]);
      }
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [vccs, customOfferId, hasVCC]);

  const isVCCPaymentTerm = paymentTermsGlobal === 'vcc';

  if (isLoading) {
    return <CustomOfferVendorVirtualCreditCardLoading />;
  }

  if (error) {
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography color="error">{error}</Typography>
      </Stack>
    );
  }

  if (!isCustomOfferAdminUser && hasVCC) {
    return <VirtualCreditCardComponent showBalance={false} />;
  }

  if (vccs && vccs.length > 0) {
    return (
      <PermissionedComponent requiredRoles={[ROLE_CUSTOM_OFFER_ADMIN_USER]}>
        <VirtualCreditCardComponent
          cardNumber={String(vccs[0].pan)}
          expiry={vccs[0].expiry}
          cvv={String(vccs[0].avv)}
          nameOnCard={brandName}
          balance={vccs[0].balance}
          currencyCode={vccs[0].currencyCode}
          state={vccs[0].state as VirtualCreditCardState}
        />
      </PermissionedComponent>
    );
  }

  if (!hasVCC && isVCCPaymentTerm) {
    return (
      <VCCForCustomOfferPreConditions
        customOfferStatus={customOfferStatus}
        onCreateVCC={handleReserveVirtualCreditCards}
        isLoading={isReservingVirtualCreditCards}
        validationErrors={validationErrors}
        reservationError={reservationError}
      />
    );
  }

  return null;
}
